'use client';
import { Button } from '@finn/design-system/atoms/button';
import { getAppSDK } from '@finn/ua-app';
import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { isMobileApp, trackEvent, useIntl } from '@finn/ui-utils';
import { compareVersions } from 'compare-versions';
import { useRouter } from 'next/compat/router';
import { signIn } from 'next-auth/react';
import React, { useEffect, useState } from 'react';

import { MatchingIcon } from './MatchingIcon';
import { SocialLoginMethod } from './SocialLoginButtons';

type ButtonProps = {
  method: SocialLoginMethod;
  cartURL: string;
};

const RESET_TIMEOUT = 3000;

export const SocialLoginButton: React.FC<ButtonProps> = ({ method }) => {
  const i18n = useIntl();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const track = useTrackingStore((state) => state.track);

  const isApp = isMobileApp();

  useEffect(() => {
    const appSDK = getAppSDK();
    if (!appSDK) {
      return;
    }

    appSDK.stopLoading = () => {
      if (loading) {
        setLoading(false);
      }
    };
  }, [loading]);

  const onButtonClick = (loginMethod: SocialLoginMethod) => {
    setLoading(true);

    if (isApp) {
      const appSDK = getAppSDK();
      const version = appSDK?.getTrackingProps().app_version;
      const isWebFirstNavigation = version
        ? compareVersions(version, '1.47.0') >= 0
        : false;
      const withNativeAuth = version
        ? compareVersions(version, '1.48.0') >= 0
        : false;

      if (withNativeAuth) {
        appSDK?.sendMessageToApp({
          type: 'event:native_social_login',
          value: loginMethod,
        });
      } else if (isWebFirstNavigation) {
        const redirectTo = router?.asPath?.includes('/checkout/')
          ? router?.asPath
          : 'account';
        window.location.assign(
          `/mobile/loginredirect?method=${loginMethod}&deepLink=${redirectTo}`
        );

        setLoading(false);
      } else {
        // for the app we created separate page which is opened in a browser and calls signIn method
        // we did this due to constraint how Next Auth works with callback URLs (keeping it in cookies)
        window.location.assign(
          `/mobile/loginredirect?method=${loginMethod}&deepLink=${
            router?.asPath?.includes('/mobile/checkout/')
              ? // remove /mobile/checkout so that app doesn't see it as checkout URL -> opens it in browser
                // ?undefined= comes from the app and is a bug, we remove it here and will fix it in next release
                router?.asPath
                  .replace('/mobile/checkout/', '')
                  .replace('?undefined=', '')
              : 'subscribe'
          }#appkey=/b2b`
        );

        setLoading(false);
      }
    } else {
      const callbackUrlObject = {};

      signIn(method.toLowerCase(), callbackUrlObject).then(() => {
        setTimeout(() => {
          setLoading(false);
        }, RESET_TIMEOUT);
      });
    }
    track(TrackingEventName.LOGIN_METHOD_CLICKED, {
      additionalProps: {
        loginMethod,
      },
    });
    trackEvent('Account Logged In', { loginMethod });
  };
  const loginText = i18n.formatMessage(`userAccount.button.${method}`);

  return (
    <Button
      loading={loading}
      onClick={() => onButtonClick(method)}
      variant="outline"
      className="border-pewter hover:bg-snow w-full hover:fill-black hover:text-black"
    >
      <span className="mr-2 mt-1 inline-block">
        <MatchingIcon method={method} />
      </span>
      <span className="body-14-semibold">{loginText}</span>
    </Button>
  );
};
